import type { PointArray } from '../types/contour-definition-array-type';

import { getPolygonIntersection } from './getPolygonIntersection';
import { insetPolygon } from './insetPolygon';
import { isPolygonInside } from './isPolygonInside';

/**
 * Returns `child` clipped so it lies inside `parent` with a small gap, so that
 * child and parent never share a boundary coordinate.
 *
 * - `parent` is first eroded inward by `gap`; the child is then constrained to
 *   that eroded region.
 * - If `child` already lies inside the eroded region, the original `child`
 *   reference is returned unchanged (callers can skip a reprocess).
 * - Otherwise `child` is intersected with the eroded region.
 * - If the result degenerates, `null` is returned.
 *
 * Both polygons must be in the same coordinate space. `gap` is in that space
 * (image pixels). Uses planar geometry, robust at image-pixel scale.
 */
export function clampContourWithinParent(
  child: PointArray,
  parent: PointArray,
  gap = 0.5,
): PointArray | null {
  if (child.length < 4 || parent.length < 4) return null;

  try {
    const region = gap > 0 ? insetPolygon(parent, gap) : parent;
    if (region.length < 4) return null;

    if (isPolygonInside(child, region)) return child;

    const clipped = getPolygonIntersection(child, region);
    if (!clipped || clipped.length < 4) return null;

    return clipped;
  }
  catch (error) {
    console.warn('clampContourWithinParent - failed to clamp contour', error);
    return null;
  }
}
